import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { DexieDbProvider } from '@shared/services/dexie-provider';
import { Organization } from '@shared/models/user-profile';
import { PreferredOrganizationRecord, PreferredOrganizationSource } from '@shared/models/preferred-organization';

/**
 * Owns the browser-level "preferred organization" (US 27702): the ONE organization this
 * browser keeps local data for and syncs. It is a property of the browser, not of a user —
 * it survives logout and applies to everyone who signs in here.
 *
 * Deliberately has no AuthService dependency (callers pass organization data in) so it can
 * be injected anywhere — including AuthService itself — without circular dependencies.
 */
@Injectable({
  providedIn: 'root'
})
export class PreferredOrganizationService {

  public static readonly STORAGE_KEY = 'edara3_preferred_organization';

  /** Emits after the preferred organization is set, changed or cleared (undefined = cleared). */
  public preferredChanged$ = new Subject<PreferredOrganizationRecord | undefined>();

  private _cached?: PreferredOrganizationRecord;
  private _cacheLoaded = false;

  constructor(private localDbProvider: DexieDbProvider) {
    // Another tab may change the preferred organization (e.g. finishing a change flow);
    // invalidate the cache so this tab's next read sees it.
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === PreferredOrganizationService.STORAGE_KEY) {
        this._cacheLoaded = false;
        this.preferredChanged$.next(this.preferred);
      }
    });
  }

  public get preferred(): PreferredOrganizationRecord | undefined {
    if (!this._cacheLoaded) {
      this._cached = this.readRecord();
      this._cacheLoaded = true;
    }
    return this._cached;
  }

  public isPreferredTenant(tenantId: string | undefined): boolean {
    return !!tenantId && this.preferred?.tenantId === tenantId;
  }

  /** True when the given user organization list contains the preferred organization. */
  public isPreferredPermittedFor(organizations: Organization[]): boolean {
    const preferred = this.preferred;
    if (!preferred) return false;
    return organizations.some(org => org.keycloakOrgId === preferred.keycloakOrgId);
  }

  public setPreferred(organization: Organization, source: PreferredOrganizationSource): void {
    if (!organization?.keycloakOrgId || !organization.tenantId) return;

    const record: PreferredOrganizationRecord = {
      keycloakOrgId: organization.keycloakOrgId,
      tenantId: organization.tenantId,
      name: organization.name,
      setAt: new Date().toISOString(),
      source: source
    };
    this.writeStorage(PreferredOrganizationService.STORAGE_KEY, JSON.stringify(record));
    this._cached = record;
    this._cacheLoaded = true;
    this.preferredChanged$.next(record);
  }

  public clearPreferred(): void {
    this.removeStorage(PreferredOrganizationService.STORAGE_KEY);
    this._cached = undefined;
    this._cacheLoaded = true;
    this.preferredChanged$.next(undefined);
  }

  /**
   * One-time adoption for browsers that were already in use before the preferred-organization
   * feature shipped: their local data belongs to the active organization, so it becomes the
   * preferred one without showing the picker or wiping anything. A genuinely fresh browser
   * (no successful sync recorded) is left untouched — the first-login flow decides there.
   */
  public async migrateIfNeeded(activeOrganization: Organization | undefined): Promise<void> {
    if (this.preferred) return;
    if (!activeOrganization?.keycloakOrgId || !activeOrganization.tenantId) return;

    if (await this.hasSyncedLocalData()) {
      this.setPreferred(activeOrganization, 'migrated');
    }
  }

  private async hasSyncedLocalData(): Promise<boolean> {
    try {
      const count = await this.localDbProvider.db.syncMetadata.count();
      return count > 0;
    } catch {
      return false;
    }
  }

  private readRecord(): PreferredOrganizationRecord | undefined {
    const raw = this.readStorage(PreferredOrganizationService.STORAGE_KEY);
    if (!raw) return undefined;

    try {
      const record = JSON.parse(raw) as PreferredOrganizationRecord;
      return record?.keycloakOrgId && record?.tenantId ? record : undefined;
    } catch {
      return undefined;
    }
  }

  private readStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage unavailable (private mode / quota); the feature degrades to asking again next login.
    }
  }

  private removeStorage(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
}
